import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Button, Drawer, IconButton, List, ListItem, ListItemText, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ReactComponent as Logo } from '../logo.svg';

const navLinks = [
  { label: 'How It Works', href: '#' },
  { label: 'Location', href: '#' },
  { label: 'Pricing', href: '#' },
  { label: 'Support', href: '#' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Helper function to get dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return '/dashboard';
    
    switch (user.role) {
      case 'host':
      case 'host_partner':
        return '/host/dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'client':
      default:
        return '/client/dashboard';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const drawer = (
    <Box sx={{ width: 250, pt: 2 }}>
      <List>
        {navLinks.map((link) => (
          <ListItem 
            key={link.label} 
            component="a" 
            href={link.href}
            sx={{ 
              color: '#e0e0e0',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: '#fff'
              }
            }}
          >
            <ListItemText primary={link.label} />
          </ListItem>
        ))}
        {user ? (
          <>
            <ListItem sx={{ mt: 2 }}>
              <Button 
                fullWidth
                sx={{ 
                  color: '#fff', 
                  fontWeight: 500, 
                  textTransform: 'none', 
                  fontSize: 16,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                onClick={() => navigate(getDashboardRoute())}
              >
                Dashboard
              </Button>
            </ListItem>
            <ListItem>
              <Button 
                fullWidth
                variant="contained" 
                sx={{ 
                  bgcolor: '#ff6b6b', 
                  color: '#fff', 
                  fontWeight: 700, 
                  borderRadius: 2, 
                  textTransform: 'none', 
                  fontSize: 16, 
                  '&:hover': { 
                    bgcolor: '#ff4c4c' 
                  } 
                }}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem sx={{ mt: 2 }}>
              <Button 
                fullWidth
                sx={{ 
                  color: '#fff', 
                  fontWeight: 500, 
                  textTransform: 'none', 
                  fontSize: 16,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                onClick={handleLogin}
              >
                Login
              </Button>
            </ListItem>
            <ListItem>
              <Button 
                fullWidth
                variant="contained" 
                sx={{ 
                  bgcolor: '#ff6b6b', 
                  color: '#fff', 
                  fontWeight: 700, 
                  borderRadius: 2, 
                  textTransform: 'none', 
                  fontSize: 16, 
                  '&:hover': { 
                    bgcolor: '#ff4c4c' 
                  } 
                }}
                onClick={handleRegister}
              >
                Sign Up
              </Button>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="absolute" sx={{ background: 'transparent', boxShadow: 'none', py: { xs: 1, md: 3 } }}>
      <Box sx={{
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        borderRadius: { xs: 0, md: 3 },
        boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
        maxWidth: 1400,
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 4 },
        py: { xs: 1, md: 1.5 },
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Toolbar 
          disableGutters 
          sx={{ 
            width: '100%', 
            minHeight: { xs: 48, md: 64 }, 
            px: 0, 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'border-box'
          }}
        >
          <Box sx={{ height: { xs: 28, md: 36 } }}>
            <Logo style={{ height: '100%', width: 'auto' }} />
          </Box>
          
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              {navLinks.map((link) => (
                <Button 
                  key={link.label} 
                  href={link.href} 
                  sx={{ 
                    color: '#e0e0e0', 
                    textTransform: 'none', 
                    fontWeight: 500, 
                    fontSize: { xs: 14, md: 16 },
                    '&:hover': {
                      color: '#fff'
                    }
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          )}

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                color: '#fff',
                p: { xs: 0.5, sm: 1 },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user ? (
                <>
                  <Button 
                    sx={{ 
                      color: '#fff', 
                      fontWeight: 500, 
                      textTransform: 'none', 
                      fontSize: { xs: 14, md: 16 },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                    onClick={() => navigate(getDashboardRoute())}
                  >
                    Dashboard
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{ 
                      bgcolor: '#ff6b6b', 
                      color: '#fff', 
                      fontWeight: 700, 
                      borderRadius: 2, 
                      textTransform: 'none', 
                      fontSize: { xs: 14, md: 16 }, 
                      '&:hover': { 
                        bgcolor: '#ff4c4c' 
                      } 
                    }}
                    onClick={handleLogout}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    sx={{ 
                      color: '#fff', 
                      fontWeight: 500, 
                      textTransform: 'none', 
                      fontSize: { xs: 14, md: 16 },
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                    onClick={handleLogin}
                  >
                    Login
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{ 
                      bgcolor: '#ff6b6b', 
                      color: '#fff', 
                      fontWeight: 700, 
                      borderRadius: 2, 
                      textTransform: 'none', 
                      fontSize: { xs: 14, md: 16 }, 
                      '&:hover': { 
                        bgcolor: '#ff4c4c' 
                      } 
                    }}
                    onClick={handleRegister}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Box>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': { 
            bgcolor: 'background.paper',
            boxSizing: 'border-box',
            width: 250,
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
} 